import { useEffect, useRef, useState } from 'react';
import * as THREE from 'three';
import { EffectComposer, RenderPass, BloomEffect, VignetteEffect, EffectPass } from 'postprocessing';
import { useTheme } from '../context/ThemeContext';

/**
 * WebGL Background Component - Working Version
 * Features:
 * - 16,384 particles using basic Three.js Points
 * - Curl noise-inspired movement
 * - Reliable rendering without complex FBO
 */

// Simple noise function for movement
const noise = (x, y, z, t) => {
  const a = Math.sin(x * 0.1 + t * 0.5) * Math.cos(y * 0.1);
  const b = Math.cos(z * 0.1 + t * 0.3) * Math.sin(x * 0.05);
  const c = Math.sin(y * 0.05 + t * 0.7) * Math.cos(z * 0.05);
  return (a + b + c) / 3;
};

class ParticleSystem {
  constructor(particleCount = 4096, particleColor = 0x64b5f6, movementSpeed = 5.0) { // Reduced speed by half
    this.particleCount = particleCount;
    this.movementSpeed = movementSpeed;
    this.positions = new Float32Array(this.particleCount * 3);
    this.velocities = new Float32Array(this.particleCount * 3);
    this.originalPositions = new Float32Array(this.particleCount * 3);
    this.particleSizes = new Float32Array(this.particleCount);  // Store individual sizes
    this.speedMultipliers = new Float32Array(this.particleCount);  // Store speed multipliers
    this.rockInstances = [];  // Store individual rock instances
    
    // Keep track of special particles
    let infinityStoneCount = 0;
    const maxInfinityStones = 5; // Exactly 5 Infinity Stones
    let mediumLargeCount = 0;
    const maxMediumLarge = 12; // Exactly 12 white/cyan stones
    
    // Initialize particles with physics-based size/speed relationship
    for (let i = 0; i < this.particleCount; i++) {
      const i3 = i * 3;
      
      // Size distribution - ensure exactly 5 infinity stones and 12 medium-large
      let particleSize;
      
      if (infinityStoneCount < maxInfinityStones && (i < maxInfinityStones || Math.random() < 0.001)) {
        // Make this a large Infinity Stone (5.0 to 8.0)
        particleSize = 5.0 + Math.random() * 3.0;
        infinityStoneCount++;
      } else if (mediumLargeCount < maxMediumLarge && (i < maxInfinityStones + maxMediumLarge || Math.random() < 0.01)) {
        // Medium-large white/cyan stones (60% of Infinity Stone size: 3.0 to 4.8)
        particleSize = 3.0 + Math.random() * 1.8;
        mediumLargeCount++;
      } else {
        // Regular distribution for other particles
        const rand = Math.random();
        if (rand < 0.3) {
          // 30% medium particles (1.0 to 2.0)
          particleSize = 1.0 + Math.random() * 1.0;
        } else {
          // 70% small particles (0.5 to 1.0)
          particleSize = 0.5 + Math.random() * 0.5;
        }
      }
      
      this.particleSizes[i] = particleSize;
      
      // Physics: smaller particles move faster (inverse relationship)
      // Size 0.5 = speed 2.0, Size 3.0 = speed 0.33
      this.speedMultipliers[i] = 1.5 / particleSize;
      
      // Create tornado/fish swarm distribution
      const swarmPattern = this.generateSwarmPosition(i);
      this.positions[i3] = swarmPattern.x;
      this.positions[i3 + 1] = swarmPattern.y;
      this.positions[i3 + 2] = swarmPattern.z;
      
      // Store original positions
      this.originalPositions[i3] = this.positions[i3];
      this.originalPositions[i3 + 1] = this.positions[i3 + 1];
      this.originalPositions[i3 + 2] = this.positions[i3 + 2];
      
      // Initial velocities scaled by size (smaller = faster)
      const baseVelocity = 1.0 * this.speedMultipliers[i];
      this.velocities[i3] = (Math.random() - 0.5) * baseVelocity;
      this.velocities[i3 + 1] = (Math.random() - 0.5) * baseVelocity;
      this.velocities[i3 + 2] = (Math.random() - 0.5) * baseVelocity;
    }
    
    // Create instanced rock geometry instead of particles
    this.createRockInstances(particleColor, 'dark'); // Default to dark, will be updated
    
    // console.log('Rock debris system created with', this.particleCount, 'rocks');
  }
  
  createRockInstances(particleColor, theme) {
    // Create various rock geometries
    const rockGeometries = this.createRockGeometries();
    
    // Create instanced mesh group
    this.rockGroup = new THREE.Group();
    
    // Infinity Stone colors for the 5 large particles
    const infinityStoneColors = [
      new THREE.Color(0x9C27B0), // Power Stone - Purple
      new THREE.Color(0x2196F3), // Space Stone - Blue
      new THREE.Color(0xF44336), // Reality Stone - Red
      new THREE.Color(0xFF9800), // Soul Stone - Orange
      new THREE.Color(0xFFEB3B), // Mind Stone - Yellow
      new THREE.Color(0x4CAF50)  // Time Stone - Green (adding 6th for variety)
    ];
    
    // Theme-aware transparency
    const opacity = theme === 'light' ? 0.7 : 1.0; // Slightly more visible in light mode
    const transparent = theme === 'light' ? true : false;
    
    // Keep track of which Infinity Stone color to use
    this.infinityStoneIndex = 0;
    
    // White/Cyan colors for medium-large particles - alternating white and cyan
    const whiteCyanColors = [
      new THREE.Color(0xFFFFFF), // Pure white 1
      new THREE.Color(0x00FFFF), // Cyan 1
      new THREE.Color(0xF8F8FF), // Ghost white 2
      new THREE.Color(0x00CED1), // Dark turquoise 2
      new THREE.Color(0xFFFAFA), // Snow white 3
      new THREE.Color(0x40E0D0), // Turquoise 3
      new THREE.Color(0xF0FFFF), // Azure white 4
      new THREE.Color(0x00BFFF), // Deep sky blue 4
      new THREE.Color(0xFFF8DC), // Cornsilk white 5
      new THREE.Color(0x5F9EA0), // Cadet blue 5
      new THREE.Color(0xFFFFF0), // Ivory white 6
      new THREE.Color(0x20B2AA), // Light sea green 6
    ];
    
    // Create materials for white/cyan stones
    this.whiteCyanMaterials = whiteCyanColors.map(color => new THREE.MeshStandardMaterial({
      color: color,
      roughness: 0.5,   // Medium smoothness
      metalness: 0.3,   // Some metallic shine
      flatShading: false,
      opacity: opacity,
      transparent: transparent
    }));
    
    this.whiteCyanIndex = 0;
    
    // Create materials for Infinity Stones
    this.infinityStoneMaterials = infinityStoneColors.map(color => new THREE.MeshStandardMaterial({
      color: color,
      roughness: 0.3,   // Smoother for gem-like appearance
      metalness: 0.8,   // More metallic for gem shine
      flatShading: false, // Smooth shading for gem look
      
      // Theme-aware transparency
      opacity: opacity,
      transparent: transparent
    }));
    
    // Lusion-style emissive material for large glowing particles with bloom
    this.glowMaterial = new THREE.MeshBasicMaterial({
      color: new THREE.Color(0x64b5f6), // Blue glow like Lusion
      transparent: true,
      opacity: 0.3, // Reduced from 0.6 to 0.3
      blending: THREE.AdditiveBlending // Key for glow effect
    });
    
    // Enhanced bloom material for massive particles (>5.0)
    this.bloomMaterial = new THREE.MeshBasicMaterial({
      color: new THREE.Color(0x88ccff), // Brighter blue for bloom
      transparent: true,
      opacity: 0.4, // Reduced from 0.8 to 0.4
      blending: THREE.AdditiveBlending
      // Note: MeshBasicMaterial doesn't support emissive properties
    });
    
    // Create individual rock instances
    for (let i = 0; i < this.particleCount; i++) {
      const i3 = i * 3;
      
      // Pick random rock geometry
      const geometry = rockGeometries[Math.floor(Math.random() * rockGeometries.length)];
      
      // Create main rock mesh - Different materials based on size
      let material;
      if (this.particleSizes[i] > 5.0) { // Large particles are Infinity Stones
        material = this.infinityStoneMaterials[this.infinityStoneIndex % this.infinityStoneMaterials.length];
        this.infinityStoneIndex++;
      } else if (this.particleSizes[i] > 3.0) { // Medium-large particles are white/cyan
        material = this.whiteCyanMaterials[this.whiteCyanIndex % this.whiteCyanMaterials.length];
        this.whiteCyanIndex++;
      } else { // Small/medium particles - black in light mode, grayish white in dark mode
        if (!this.smallParticleMaterial) {
          this.smallParticleMaterial = new THREE.MeshStandardMaterial({
            color: theme === 'light' 
              ? new THREE.Color(0x000000)  // Pitch black for light mode
              : new THREE.Color(0xd1d5db), // Light grayish white for dark mode (original)
            roughness: 0.9,
            metalness: 0.02,
            opacity: theme === 'light' ? 0.8 : 1.0,
            transparent: theme === 'light' ? true : false
          });
        }
        material = this.smallParticleMaterial;
      }
      const rockMesh = new THREE.Mesh(geometry, material);
      
      // Set position
      rockMesh.position.set(
        this.positions[i3],
        this.positions[i3 + 1], 
        this.positions[i3 + 2]
      );
      
      // Set scale based on particle size
      const scale = this.particleSizes[i] * 0.5;
      rockMesh.scale.set(scale, scale, scale);
      
      // Start invisible for fade-in after 1 second
      rockMesh.visible = false;
      rockMesh.userData.spawnTime = 1.0; // 1 second delay
      rockMesh.userData.fadeInSpeed = 0.04; // Faster fade in speed
      
      // Random rotation
      rockMesh.rotation.set(
        Math.random() * Math.PI * 2,
        Math.random() * Math.PI * 2,
        Math.random() * Math.PI * 2
      );
      
      // Enable shadows for UFO lighting effect
      rockMesh.castShadow = true; // Rocks cast shadows
      rockMesh.receiveShadow = true; // Rocks receive shadows from other rocks
      
      // Add bloom for massive particles (size > 5.0) - match their color
      if (this.particleSizes[i] > 5.0) {
        // Create bloom that matches the Infinity Stone color
        const bloomGeometry = geometry.clone();
        const bloomColor = material.color.clone();
        
        // Reuse bloom material if color matches
        let bloomMat;
        const colorKey = bloomColor.getHexString();
        if (!this.bloomMaterialCache) this.bloomMaterialCache = {};
        
        if (this.bloomMaterialCache[colorKey]) {
          bloomMat = this.bloomMaterialCache[colorKey];
        } else {
          bloomMat = new THREE.MeshBasicMaterial({
            color: bloomColor,
            transparent: true,
            opacity: 0.3,
            blending: THREE.AdditiveBlending
          });
          this.bloomMaterialCache[colorKey] = bloomMat;
        }
        
        const bloomGlow = new THREE.Mesh(bloomGeometry, bloomMat);
        bloomGlow.position.copy(rockMesh.position);
        bloomGlow.rotation.copy(rockMesh.rotation);
        const bloomScale = scale * 1.8; // Much larger bloom
        bloomGlow.scale.set(bloomScale, bloomScale, bloomScale);
        
        rockMesh.userData.bloomMesh = bloomGlow;
        bloomGlow.visible = false; // Start invisible for fade-in
        this.rockGroup.add(bloomGlow);
      }
      
      this.rockGroup.add(rockMesh);
      this.rockInstances.push(rockMesh);
    }
    
    this.mesh = this.rockGroup;
  }
  
  updateTheme(theme) {
    // Update small particle material color based on theme
    if (this.smallParticleMaterial) {
      const smallParticleColor = theme === 'light' 
        ? new THREE.Color(0x000000)  // Pitch black for light mode
        : new THREE.Color(0xd1d5db); // Light grayish white for dark mode
      
      // Theme-aware transparency
      const opacity = theme === 'light' ? 0.8 : 1.0;
      const transparent = theme === 'light' ? true : false;
      
      this.smallParticleMaterial.color = smallParticleColor;
      this.smallParticleMaterial.opacity = opacity;
      this.smallParticleMaterial.transparent = transparent;
      this.smallParticleMaterial.needsUpdate = true;
    }
    
    // Update Infinity Stone materials transparency
    if (this.infinityStoneMaterials) {
      this.infinityStoneMaterials.forEach(material => {
        material.opacity = theme === 'light' ? 0.7 : 1.0;
        material.transparent = theme === 'light' ? true : false;
        material.needsUpdate = true;
      });
    }
  }
  
  createRockGeometries() {
    const geometries = [];
    
    // Create optimized rock shapes with lower detail
    for (let i = 0; i < 4; i++) { // Reduced from 6 to 4 variations
      const baseSize = 0.4 + Math.random() * 0.4; // 0.4 to 0.8 (larger base size)
      const widthSegments = 8; // Reduced from 16 for performance
      const heightSegments = 6; // Reduced from 12 for performance
      
      const geometry = new THREE.SphereGeometry(baseSize, widthSegments, heightSegments);
      
      // Lighter deformation for performance
      this.randomizeGeometry(geometry, 0.1 + Math.random() * 0.05); // Reduced chaos
      
      geometries.push(geometry);
    }
    
    return geometries;
  }
  
  randomizeGeometry(geometry, intensity) {
    // Balanced randomization - not too sharp, not too smooth
    const positions = geometry.attributes.position.array;
    
    for (let i = 0; i < positions.length; i += 3) {
      const x = positions[i];
      const y = positions[i + 1];
      const z = positions[i + 2];
      
      // Add moderate surface variation
      const noiseX = Math.sin(x * 5) * Math.cos(y * 4) * 0.08;
      const noiseY = Math.cos(y * 5) * Math.sin(z * 4) * 0.08;
      const noiseZ = Math.sin(z * 5) * Math.cos(x * 4) * 0.08;
      
      positions[i] += (Math.random() - 0.5) * intensity + noiseX;
      positions[i + 1] += (Math.random() - 0.5) * intensity + noiseY;
      positions[i + 2] += (Math.random() - 0.5) * intensity + noiseZ;
    }
    
    geometry.attributes.position.needsUpdate = true;
    geometry.computeVertexNormals(); // Recalculate normals for lighting
  }
  
  generateSwarmPosition(index) {
    // Create spherical swarm pattern (globe distribution)
    const normalizedIndex = index / this.particleCount;
    
    // 80% particles form the main spherical cloud
    if (normalizedIndex < 0.8) {
      // Generate random point inside sphere using uniform distribution
      const radius = Math.pow(Math.random(), 1/3) * 30; // Cube root for uniform volume
      const theta = Math.random() * Math.PI * 2; // Azimuth angle
      const phi = Math.acos(2 * Math.random() - 1); // Polar angle (uniform on sphere)
      
      const x = radius * Math.sin(phi) * Math.cos(theta);
      const y = radius * Math.sin(phi) * Math.sin(theta);
      const z = radius * Math.cos(phi);
      
      return { x, y, z };
      
    } else {
      // 20% particles in outer shell for depth
      const radius = 40 + Math.random() * 20; // 40-60 radius
      const theta = Math.random() * Math.PI * 2;
      const phi = Math.acos(2 * Math.random() - 1);
      
      const x = radius * Math.sin(phi) * Math.cos(theta);
      const y = radius * Math.sin(phi) * Math.sin(theta);
      const z = radius * Math.cos(phi);
      
      return { x, y, z };
    }
  }
  
  update(time, deltaTime, theme = 'dark') {
    // Dynamic pattern switching: Fire vs Tornado
    const cycleTime = time * 0.1; // 10-second cycles
    const patternBlend = (Math.sin(cycleTime) + 1) * 0.5; // 0 = full fire, 1 = full tornado
    
    // Update each rock instance
    for (let i = 0; i < this.particleCount; i++) {
      const i3 = i * 3;
      const rock = this.rockInstances[i];
      
      if (!rock) continue;
      
      const x = this.positions[i3];
      const y = this.positions[i3 + 1];
      const z = this.positions[i3 + 2];
      const rockSpeed = this.speedMultipliers[i];
      
      // FIRE PATTERN: Upward flowing with chaotic dispersal
      const fireHeight = y + 40; // Bottom to top
      const fireIntensity = Math.max(0, 1 - fireHeight / 80); // Strong at bottom, weak at top
      const fireUpward = fireIntensity * 1.5 * rockSpeed; // Strong upward flow
      const fireSpread = noise(x * 2, z * 2, time * 2, 0) * fireIntensity * rockSpeed; // Chaotic spreading
      
      const fireX = fireSpread + noise(y * 0.5, time * 3, i * 0.1, 0) * 0.8 * rockSpeed;
      const fireY = fireUpward + noise(x, z, time * 2, 0) * 0.6 * rockSpeed;
      const fireZ = fireSpread + noise(y * 0.5, time * 3, i * 0.2, 0) * 0.8 * rockSpeed;
      
      // TORNADO PATTERN: Tight spiral with strong vertical flow
      const distance = Math.sqrt(x * x + z * z) + 0.1;
      const tornadoHeight = (y + 40) / 80; // 0 at bottom, 1 at top
      // eslint-disable-next-line no-unused-vars
      const tornadoRadius = 5 + tornadoHeight * 30; // Widens from bottom to top - reserved for future tornado expansion
      const tornadoSpeed = 0.8 / Math.sqrt(distance); // Faster closer to center
      
      const tornadoX = -z * tornadoSpeed * rockSpeed;
      const tornadoY = (1.2 + Math.sin(Math.atan2(z, x) * 3 + time)) * rockSpeed; // Spiral upward
      const tornadoZ = x * tornadoSpeed * rockSpeed;
      
      // BLEND PATTERNS based on cycle
      const blendX = fireX * (1 - patternBlend) + tornadoX * patternBlend;
      const blendY = fireY * (1 - patternBlend) + tornadoY * patternBlend;
      const blendZ = fireZ * (1 - patternBlend) + tornadoZ * patternBlend;
      
      // Add subtle organic variation
      const organicX = noise(y * 0.3, z * 0.3, time * 0.5, time) * 0.3 * rockSpeed;
      const organicY = noise(z * 0.3, x * 0.3, time * 0.5, time) * 0.3 * rockSpeed;
      const organicZ = noise(x * 0.3, y * 0.3, time * 0.5, time) * 0.3 * rockSpeed;
      
      // Final combined movement
      this.velocities[i3] += (blendX + organicX) * deltaTime;
      this.velocities[i3 + 1] += (blendY + organicY) * deltaTime;
      this.velocities[i3 + 2] += (blendZ + organicZ) * deltaTime;
      
      // Apply damping
      this.velocities[i3] *= 0.98;
      this.velocities[i3 + 1] *= 0.98;
      this.velocities[i3 + 2] *= 0.98;
      
      // Update positions
      this.positions[i3] += this.velocities[i3];
      this.positions[i3 + 1] += this.velocities[i3 + 1];
      this.positions[i3 + 2] += this.velocities[i3 + 2];
      
      // Update rock mesh position
      rock.position.set(this.positions[i3], this.positions[i3 + 1], this.positions[i3 + 2]);
      
      // Handle spawn delay and fade-in
      if (rock.userData.spawnTime !== undefined) {
        if (rock.userData.spawnTime > 0) {
          // Still in delay period
          rock.userData.spawnTime -= deltaTime;
          if (rock.userData.bloomMesh) {
            rock.userData.bloomMesh.visible = false;
          }
        } else {
          // Start fading in after delay
          if (!rock.visible) {
            rock.visible = true;
            rock.material.opacity = 0;
            rock.material.transparent = true;
            if (rock.userData.bloomMesh) {
              rock.userData.bloomMesh.visible = true;
              rock.userData.bloomMesh.material.opacity = 0;
            }
          }
          
          // Fade in animation
          if (rock.material.opacity < (theme === 'light' ? 0.7 : 1.0)) {
            rock.material.opacity = Math.min(
              rock.material.opacity + rock.userData.fadeInSpeed,
              theme === 'light' ? 0.7 : 1.0
            );
            
            // Fade in bloom as well
            if (rock.userData.bloomMesh) {
              rock.userData.bloomMesh.material.opacity = Math.min(
                rock.userData.bloomMesh.material.opacity + rock.userData.fadeInSpeed * 0.5,
                0.3
              );
            }
          }
        }
      }
      
      // Add rotation to rocks for realistic tumbling
      const rotationSpeed = rockSpeed * 0.5;
      rock.rotation.x += rotationSpeed * deltaTime;
      rock.rotation.y += rotationSpeed * deltaTime * 0.7;
      rock.rotation.z += rotationSpeed * deltaTime * 0.3;
      
      // No more middle shell glow - removed completely
      
      // Update bloom mesh if it exists (for massive particles)
      if (rock.userData.bloomMesh) {
        rock.userData.bloomMesh.position.copy(rock.position);
        rock.userData.bloomMesh.rotation.copy(rock.rotation);
        
        // Stronger bloom effect - pulsing with pattern and movement
        const bloomIntensity = 0.6 + (patternBlend * 0.6); // Strong bloom during tornado
        const bloomPulse = Math.sin(time * 2 + i * 0.05) * 0.3 + 0.7; // 0.4-1.0 range
        rock.userData.bloomMesh.material.opacity = bloomIntensity * bloomPulse;
        
        // Scale bloom slightly for breathing effect
        const breathScale = 1 + Math.sin(time * 1.5 + i * 0.1) * 0.1; // ±10% size variation
        const currentScale = this.particleSizes[i] * 0.5 * 1.8 * breathScale;
        rock.userData.bloomMesh.scale.set(currentScale, currentScale, currentScale);
      }
      
      // Fuzzy spherical boundary with leak spikes
      const boundaryDistance = Math.sqrt(
        this.positions[i3] * this.positions[i3] + 
        this.positions[i3 + 1] * this.positions[i3 + 1] + 
        this.positions[i3 + 2] * this.positions[i3 + 2]
      );
      
      // Very soft, organic boundary - almost invisible
      const baseRadius = 80;
      const fuzziness = 25; // Increased fuzziness ±25 units
      const organicNoise = noise(i * 0.1, time * 0.1, 0, 0); // Time-varying boundary
      
      // Each particle gets its own floating boundary (changes over time)
      const particleBoundary = baseRadius + 
        (Math.sin(i * 0.123 + time * 0.1) * fuzziness) + // Time-varying
        (Math.cos(i * 0.456 + time * 0.05) * fuzziness * 0.7) +
        (organicNoise * fuzziness * 0.3); // Organic noise variation
      
      // More particles can leak, with varying distances
      const leakFactor = Math.sin(i * 0.789 + time * 0.03) * 0.5 + 0.5; // 0-1
      const isLeakSpike = leakFactor > 0.7; // 30% of particles can leak at any time
      const leakDistance = baseRadius * (1.2 + leakFactor * 0.8); // 20-100% further
      const leakRadius = isLeakSpike ? leakDistance : particleBoundary;
      
      if (boundaryDistance > leakRadius) {
        // Very gentle, gradual attraction - almost imperceptible
        const overshoot = boundaryDistance - leakRadius;
        const pullForce = Math.min(overshoot / 50, 1.0) * 0.02; // Much gentler pull
        
        // Normalize direction vector toward center
        const centerX = -this.positions[i3] / boundaryDistance;
        const centerY = -this.positions[i3 + 1] / boundaryDistance;
        const centerZ = -this.positions[i3 + 2] / boundaryDistance;
        
        // Apply very subtle attraction force toward center
        this.velocities[i3] += centerX * pullForce;
        this.velocities[i3 + 1] += centerY * pullForce;
        this.velocities[i3 + 2] += centerZ * pullForce;
        
        // Minimal velocity dampening only when extremely far out
        if (overshoot > 40) {
          this.velocities[i3] *= 0.98; // Very light dampening
          this.velocities[i3 + 1] *= 0.98;
          this.velocities[i3 + 2] *= 0.98;
        }
      }
    }
    
    // No animated lights needed
  }
  
  dispose() {
    // Dispose rock geometries and materials
    if (this.rockMaterial) {
      this.rockMaterial.dispose();
    }
    
    // Dispose each rock instance
    this.rockInstances.forEach(rock => {
      if (rock.geometry) rock.geometry.dispose();
    });
  }
}

class WebGLApp {
  constructor(particleCount = 4096, particleColor = 0x64b5f6, movementSpeed = 5.0, cameraDistance = 100, theme = 'dark') { // Reduced speed by half
    this.particleCount = particleCount;
    this.particleColor = particleColor;
    this.movementSpeed = movementSpeed;
    this.cameraDistance = cameraDistance;
    this.theme = theme;
    this.scene = null;
    this.camera = null;
    this.renderer = null;
    this.composer = null;
    this.canvas = null;
    this.particleSystem = null;
    this.time = 0;
    this.lastTime = 0;
    this.animationId = null;
    
    // Mouse wheel scroll rotation and zoom
    this.swarmRotation = { x: 0, y: 0, z: 0 };
    this.targetRotation = { x: 0, y: 0, z: 0 };
    this.scrollAccumulator = 0;
    this.baseZoom = this.cameraDistance;
    this.currentZoom = this.baseZoom;
    this.targetZoom = this.baseZoom;
    this.maxZoomIn = this.baseZoom * 0.8; // 20% closer (zoom in)
    this.maxZoomOut = this.baseZoom * 1.2; // 20% further (zoom out)
    
    // Performance settings with device awareness
    this.isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
    this.isTablet = this.isMobile && (window.innerWidth >= 768 && window.innerWidth <= 1024);
    this.isDesktop = !this.isMobile;
    this.pixelRatio = Math.min(window.devicePixelRatio, this.isMobile ? 1.5 : 2);
    
    // Device-specific settings for special effects
    this.deviceSettings = {
      mobile: {
        particleCount: 2048,  // Half particles
        enableBloom: false,   // No bloom for performance
        enableShadows: false, // No shadows
        ufoIntensity: 2.0,    // Lower UFO light intensity
        effectQuality: 'low'
      },
      tablet: {
        particleCount: 3072,  // 75% particles
        enableBloom: true,    // Light bloom
        enableShadows: false, // No shadows
        ufoIntensity: 3.0,    // Medium UFO light
        effectQuality: 'medium'
      },
      desktop: {
        particleCount: 4096,  // Full particles
        enableBloom: true,    // Full bloom
        enableShadows: true,  // Full shadows
        ufoIntensity: 4.0,    // Full UFO light
        effectQuality: 'high'
      }
    };
  }

  setCanvas(canvas) {
    this.canvas = canvas;
    return this.initWebGL();
  }

  initWebGL() {
    try {
      const gl = this.canvas.getContext('webgl2') || this.canvas.getContext('webgl');
      if (!gl) {
        console.warn('WebGL not supported');
        return false;
      }
      
      // console.log('WebGL initialized successfully');
      return true;
    } catch (error) {
      console.warn('WebGL initialization failed:', error);
      return false;
    }
  }

  init() {
    // Get device-specific settings
    const currentDevice = this.isMobile ? (this.isTablet ? 'tablet' : 'mobile') : 'desktop';
    const settings = this.deviceSettings[currentDevice];
    
    // console.log(`Initializing particle system for ${currentDevice} with ${settings.effectQuality} quality`);
    
    // Scene setup
    this.scene = new THREE.Scene();
    
    // Camera setup
    this.camera = new THREE.PerspectiveCamera(
      75,
      window.innerWidth / window.innerHeight,
      0.1,
      1000
    );
    this.camera.position.z = this.cameraDistance;

    // Renderer setup with enhanced antialiasing and shadows
    this.renderer = new THREE.WebGLRenderer({
      canvas: this.canvas,
      alpha: true,
      antialias: true,  // Force antialiasing
      powerPreference: 'high-performance',
      premultipliedAlpha: false,
      preserveDrawingBuffer: false
    });
    
    // Device-aware shadow settings
    this.renderer.shadowMap.enabled = settings.enableShadows;
    if (settings.enableShadows) {
      this.renderer.shadowMap.type = THREE.PCFSoftShadowMap; // Soft shadows
    }
    
    this.renderer.setSize(window.innerWidth, window.innerHeight);
    this.renderer.setPixelRatio(this.pixelRatio);
    this.renderer.setClearColor(0x000000, 0);
    
    // console.log('Renderer created');

    // Create particle system with device-aware settings
    this.particleSystem = new ParticleSystem(settings.particleCount, this.particleColor, this.movementSpeed);
    // Update with current theme
    this.particleSystem.updateTheme(this.theme);
    this.scene.add(this.particleSystem.mesh);
    // console.log('Particles added to scene');
    
    // UFO-style top-down lighting system
    const ambientLight = new THREE.AmbientLight(0x202030, 0.2); // Dark ambient
    this.scene.add(ambientLight);
    
    // Main UFO beam - device-aware intensity
    this.ufoSpotlight = new THREE.SpotLight(0x88ddff, settings.ufoIntensity); // Device-aware UFO beam
    this.ufoSpotlight.position.set(0, 120, 0); // High above center
    this.ufoSpotlight.target.position.set(0, 0, 0); // Pointing at swarm center
    this.ufoSpotlight.angle = Math.PI / 3; // 60-degree cone
    this.ufoSpotlight.penumbra = 0.4; // Soft edge transition
    this.ufoSpotlight.decay = 0.3; // Light falloff
    this.ufoSpotlight.distance = 180; // Reach
    // Device-aware shadow casting
    this.ufoSpotlight.castShadow = settings.enableShadows;
    if (settings.enableShadows) {
      this.ufoSpotlight.shadow.mapSize.width = settings.effectQuality === 'high' ? 2048 : 1024;
      this.ufoSpotlight.shadow.mapSize.height = settings.effectQuality === 'high' ? 2048 : 1024;
      this.ufoSpotlight.shadow.camera.near = 50;
      this.ufoSpotlight.shadow.camera.far = 200;
    }
    this.scene.add(this.ufoSpotlight);
    this.scene.add(this.ufoSpotlight.target);
    
    // Secondary atmospheric lighting
    const sideLight1 = new THREE.DirectionalLight(0x4466ff, 0.6); // Cool blue side
    sideLight1.position.set(-60, 40, 30);
    this.scene.add(sideLight1);
    
    const sideLight2 = new THREE.DirectionalLight(0xff6644, 0.4); // Warm orange side
    sideLight2.position.set(60, 20, -30);
    this.scene.add(sideLight2);
    
    // No center sun - removed
    
    // Setup postprocessing
    this.setupPostprocessing();
    // console.log('Postprocessing setup complete');
    
    // Setup mouse wheel scroll listener
    this.setupScrollListener();
    
    // Start animation loop
    this.lastTime = performance.now() / 1000;
    this.animate();
    // console.log('Animation loop started');
  }

  addLuminousSun() {
    // Create luminous sun at center (0,0,0) with dispersed circular light
    
    // Core sun sphere
    const sunGeometry = new THREE.SphereGeometry(3, 32, 32);
    const sunMaterial = new THREE.MeshBasicMaterial({
      color: new THREE.Color(0xffdd44), // Warm golden sun color
      transparent: true,
      opacity: 0.9,
      emissive: new THREE.Color(0xffaa00),
      emissiveIntensity: 1.2
    });
    this.sun = new THREE.Mesh(sunGeometry, sunMaterial);
    this.sun.position.set(0, 0, 0); // Center of page/scene
    this.scene.add(this.sun);
    
    // Dispersed circular light rings (multiple layers)
    const lightRings = [];
    for (let i = 0; i < 3; i++) {
      const ringGeometry = new THREE.RingGeometry(8 + i * 4, 12 + i * 4, 32);
      const ringMaterial = new THREE.MeshBasicMaterial({
        color: new THREE.Color(0xffdd44),
        transparent: true,
        opacity: 0.1 - i * 0.02, // Decreasing opacity for outer rings
        side: THREE.DoubleSide,
        blending: THREE.AdditiveBlending
      });
      const ring = new THREE.Mesh(ringGeometry, ringMaterial);
      ring.position.set(0, 0, 0);
      ring.lookAt(this.camera.position); // Face camera
      lightRings.push(ring);
      this.scene.add(ring);
    }
    
    // Store for animation
    this.sunLightRings = lightRings;
  }

  setupScrollListener() {
    // Mouse wheel scroll event listener - REMOVED preventDefault to allow page scrolling
    const handleWheel = (event) => {
      // DON'T prevent default - allow normal page scrolling
      // event.preventDefault(); // REMOVED
      
      // Accumulate scroll for smooth rotation
      this.scrollAccumulator += event.deltaY * 0.001; // Sensitivity control
      
      // Update target rotation based on scroll
      this.targetRotation.y = this.scrollAccumulator; // Primary Y rotation
      this.targetRotation.x = Math.sin(this.scrollAccumulator * 0.5) * 0.3; // Subtle X tilt
      this.targetRotation.z = Math.cos(this.scrollAccumulator * 0.3) * 0.2; // Subtle Z tilt
      
      // Zoom control based on scroll direction
      const scrollDelta = event.deltaY * 0.002; // Zoom sensitivity
      this.targetZoom += scrollDelta * this.baseZoom * 0.2; // 20% zoom range
      
      // Clamp zoom within limits
      this.targetZoom = Math.max(this.maxZoomIn, Math.min(this.maxZoomOut, this.targetZoom));
    };
    
    // Add scroll listener to window - changed to passive: true for better performance
    window.addEventListener('wheel', handleWheel, { passive: true });
    
    // Store reference for cleanup
    this.wheelHandler = handleWheel;
  }

  setupPostprocessing() {
    try {
      this.composer = new EffectComposer(this.renderer);
      
      const renderPass = new RenderPass(this.scene, this.camera);
      this.composer.addPass(renderPass);
      
      const effects = [];
      
      // Bloom effect for desktop
      if (!this.isMobile) {
        try {
          const bloomEffect = new BloomEffect({
            intensity: 1.2,
            luminanceThreshold: 0.0,
            luminanceSmoothing: 0.9,
            height: 480
          });
          effects.push(bloomEffect);
          // console.log('Bloom effect added');
        } catch (error) {
          console.warn('Failed to create bloom effect:', error);
        }
      }
      
      // Vignette effect
      try {
        const vignetteEffect = new VignetteEffect({
          offset: 0.4,
          darkness: 0.3
        });
        effects.push(vignetteEffect);
        // console.log('Vignette effect added');
      } catch (error) {
        console.warn('Failed to create vignette effect:', error);
      }
      
      if (effects.length > 0) {
        const effectPass = new EffectPass(this.camera, ...effects);
        this.composer.addPass(effectPass);
        // console.log('Effect pass added with', effects.length, 'effects');
      }
    } catch (error) {
      console.warn('Failed to setup postprocessing:', error);
      this.composer = null;
    }
  }

  animate() {
    this.animationId = requestAnimationFrame(() => this.animate());
    
    const currentTime = performance.now() / 1000;
    const deltaTime = Math.min(currentTime - this.lastTime, 0.1); // Cap delta time
    this.lastTime = currentTime;
    this.time = currentTime;
    
    // Smooth rotation and zoom interpolation
    this.swarmRotation.x += (this.targetRotation.x - this.swarmRotation.x) * 0.05;
    this.swarmRotation.y += (this.targetRotation.y - this.swarmRotation.y) * 0.05;
    this.swarmRotation.z += (this.targetRotation.z - this.swarmRotation.z) * 0.05;
    
    // Smooth zoom interpolation
    this.currentZoom += (this.targetZoom - this.currentZoom) * 0.08;
    
    // Update particles
    if (this.particleSystem) {
      this.particleSystem.update(this.time, deltaTime, this.theme);
      
      // Apply scroll-based rotation to the entire swarm
      this.particleSystem.mesh.rotation.x = this.swarmRotation.x;
      this.particleSystem.mesh.rotation.y = this.swarmRotation.y;
      this.particleSystem.mesh.rotation.z = this.swarmRotation.z;
    }
    
    // Animate UFO spotlight for dynamic light ray effects
    if (this.ufoSpotlight) {
      // Slightly move the UFO beam position for animated light rays
      const beamSway = Math.sin(this.time * 0.8) * 3; // ±3 units sway
      const beamDrift = Math.cos(this.time * 0.6) * 2; // ±2 units drift
      this.ufoSpotlight.position.x = beamSway;
      this.ufoSpotlight.position.z = beamDrift;
      
      // Animate beam intensity
      const beamPulse = Math.sin(this.time * 1.2) * 0.5 + 3.5; // 3.0-4.0 intensity
      this.ufoSpotlight.intensity = beamPulse;
      
      // Animate beam cone angle for dramatic effect
      const coneFlicker = Math.sin(this.time * 2.1) * 0.1 + (Math.PI / 3); // Slight cone variation
      this.ufoSpotlight.angle = coneFlicker;
    }
    
    // No sun animation - removed
    
    // Gentle camera rotation with dynamic zoom
    this.camera.position.x = Math.cos(this.time * 0.05) * 1;
    this.camera.position.y = Math.sin(this.time * 0.08) * 0.5;
    this.camera.position.z = this.currentZoom; // Apply zoom
    this.camera.lookAt(0, 0, 0);
    
    // Render scene
    if (this.composer) {
      this.composer.render();
    } else {
      this.renderer.render(this.scene, this.camera);
    }
  }

  resize(width, height) {
    if (!this.camera || !this.renderer) return;
    
    this.camera.aspect = width / height;
    this.camera.updateProjectionMatrix();
    this.renderer.setSize(width, height);
    
    if (this.composer) {
      this.composer.setSize(width, height);
    }
  }

  dispose() {
    if (this.animationId) {
      cancelAnimationFrame(this.animationId);
    }

    if (this.particleSystem) {
      this.particleSystem.dispose();
    }

    if (this.composer) {
      this.composer.dispose();
    }

    if (this.renderer) {
      this.renderer.dispose();
    }
    
    // Remove scroll listener
    if (this.wheelHandler) {
      window.removeEventListener('wheel', this.wheelHandler);
    }
  }
}

const WebGLBackground = ({ 
  particleCount = 4096, // Reduced for performance
  particleColor = 0x64b5f6,
  movementSpeed = 5.0, // Reduced speed by half
  cameraDistance = 100,
  style = {},
  enabled = true
}) => {
  const canvasRef = useRef(null);
  const webglAppRef = useRef(null);
  const [isSupported, setIsSupported] = useState(true);
  const { theme } = useTheme();
  
  // Enable particles in both light and dark modes
  const particlesEnabled = enabled;

  useEffect(() => {
    if (!particlesEnabled) return;
    
    if (!canvasRef.current) {
      console.warn('Canvas ref not available');
      return;
    }

    // console.log('Initializing WebGL background...');
    
    const webglApp = new WebGLApp(particleCount, particleColor, movementSpeed, cameraDistance, theme);
    webglAppRef.current = webglApp;

    const supported = webglApp.setCanvas(canvasRef.current);
    setIsSupported(supported);

    if (supported) {
      try {
        webglApp.init();

        const handleResize = () => {
          webglApp.resize(window.innerWidth, window.innerHeight);
        };

        window.addEventListener('resize', handleResize, { passive: true });

        return () => {
          window.removeEventListener('resize', handleResize);
          webglApp.dispose();
        };
      } catch (error) {
        console.error('Failed to initialize WebGL app:', error);
        setIsSupported(false);
      }
    }
  }, [particlesEnabled, particleCount, particleColor, movementSpeed, cameraDistance, theme]);
  
  // Update theme when it changes
  useEffect(() => {
    if (webglAppRef.current && webglAppRef.current.particleSystem) {
      webglAppRef.current.particleSystem.updateTheme(theme);
      webglAppRef.current.theme = theme;
      
      // No lighting updates needed - using simple directional light
    }
  }, [theme]);

  if (!particlesEnabled) {
    return null;
  }

  if (!isSupported) {
    return (
      <div className="webgl-background-fallback" />
    );
  }

  return (
    <>
      <canvas
        ref={canvasRef}
        className="webgl-background"
        style={style}
      />
      
    </>
  );
};

export default WebGLBackground;